import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, Plus, Edit, Trash2, Clock, Image, Calendar, Eye, EyeOff, Megaphone, Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Announcements() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    popup_duration_hours: 24,
    expires_at: '',
    is_active: true
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements`, getAuthHeader());
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Duyurular yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image_url: '',
      popup_duration_hours: 24,
      expires_at: '',
      is_active: true
    });
  };

  const handleImageUpload = async (file, isEdit = false) => {
    if (!file) return;
    
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Sadece PNG, JPG, WEBP veya GIF dosyaları desteklenmektedir');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/announcements/upload-temp-image`,
        formDataUpload,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success('Görsel yüklendi');
    } catch (error) {
      toast.error('Görsel yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Başlık ve içerik zorunludur');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/announcements`, {
        ...formData,
        expires_at: formData.expires_at || null
      }, getAuthHeader());
      toast.success('Duyuru oluşturuldu');
      setCreateDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error('Duyuru oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editingAnnouncement) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/announcements/${editingAnnouncement.id}`, {
        ...formData,
        expires_at: formData.expires_at || null
      }, getAuthHeader());
      toast.success('Duyuru güncellendi');
      setEditDialogOpen(false);
      setEditingAnnouncement(null);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error('Duyuru güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, getAuthHeader());
      toast.success('Duyuru silindi');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Duyuru silinemedi');
    }
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      image_url: announcement.image_url || '',
      popup_duration_hours: announcement.popup_duration_hours || 24,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : '',
      is_active: announcement.is_active
    });
    setEditDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="announcements-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl text-white">
              <Megaphone className="h-6 w-6" />
            </div>
            Duyurular
          </h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin() ? 'Duyuruları yönetin ve personele bildirim gönderin' : 'Güncel duyuruları görüntüleyin'}
          </p>
        </div>
        
        {/* Admin: Create Button */}
        {isAdmin() && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-announcement-btn"
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Duyuru
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5" />
                  Yeni Duyuru Oluştur
                </DialogTitle>
                <DialogDescription>
                  Tüm personele gösterilecek yeni bir duyuru oluşturun
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Başlık *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Duyuru başlığı"
                  />
                </div>
                <div className="space-y-2">
                  <Label>İçerik *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Duyuru içeriği..."
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Görsel (Opsiyonel)
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                  />
                  {formData.image_url ? (
                    <div className="relative">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span>Yükleniyor...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Görsel yüklemek için tıklayın
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            PNG, JPG, WEBP veya GIF (max 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Pop-up Süresi
                    </Label>
                    <Select 
                      value={String(formData.popup_duration_hours)} 
                      onValueChange={(v) => setFormData({ ...formData, popup_duration_hours: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6">6 Saat</SelectItem>
                        <SelectItem value="12">12 Saat</SelectItem>
                        <SelectItem value="24">1 Gün</SelectItem>
                        <SelectItem value="48">2 Gün</SelectItem>
                        <SelectItem value="72">3 Gün</SelectItem>
                        <SelectItem value="168">1 Hafta</SelectItem>
                        <SelectItem value="720">1 Ay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Bitiş Tarihi
                    </Label>
                    <Input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between border rounded-lg p-3">
                  <div>
                    <Label>Aktif</Label>
                    <p className="text-xs text-muted-foreground">Duyuruyu hemen yayınla</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button onClick={handleCreate} disabled={saving || !formData.title || !formData.content}>
                  {saving ? 'Oluşturuluyor...' : 'Duyuru Oluştur'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">Henüz duyuru yok</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`overflow-hidden transition-all ${
                !announcement.is_active || isExpired(announcement.expires_at) 
                  ? 'opacity-60 border-dashed' 
                  : 'hover:shadow-lg'
              }`}
            >
              <div className="flex">
                {/* Image */}
                {announcement.image_url && (
                  <div className="w-48 h-40 flex-shrink-0">
                    <img 
                      src={announcement.image_url} 
                      alt={announcement.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {announcement.is_active && !isExpired(announcement.expires_at) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            <Eye className="h-3 w-3" /> Aktif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
                            <EyeOff className="h-3 w-3" /> {isExpired(announcement.expires_at) ? 'Süresi Doldu' : 'Pasif'}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Pop-up: {announcement.popup_duration_hours} saat
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg">{announcement.title}</h3>
                    </div>
                    
                    {/* Admin Actions */}
                    {isAdmin() && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(announcement)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(announcement.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-muted-foreground mt-2 line-clamp-2">{announcement.content}</p>
                  
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span>Oluşturulma: {formatDate(announcement.created_at)}</span>
                    {announcement.expires_at && (
                      <span className={isExpired(announcement.expires_at) ? 'text-red-500' : ''}>
                        Bitiş: {formatDate(announcement.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Duyuru Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>İçerik *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Görsel
              </Label>
              <input
                type="file"
                ref={editFileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => handleImageUpload(e.target.files[0], true)}
              />
              {formData.image_url ? (
                <div className="relative">
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => editFileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => editFileInputRef.current?.click()}
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span>Yükleniyor...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Görsel yüklemek için tıklayın
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pop-up Süresi</Label>
                <Select 
                  value={String(formData.popup_duration_hours)} 
                  onValueChange={(v) => setFormData({ ...formData, popup_duration_hours: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 Saat</SelectItem>
                    <SelectItem value="12">12 Saat</SelectItem>
                    <SelectItem value="24">1 Gün</SelectItem>
                    <SelectItem value="48">2 Gün</SelectItem>
                    <SelectItem value="72">3 Gün</SelectItem>
                    <SelectItem value="168">1 Hafta</SelectItem>
                    <SelectItem value="720">1 Ay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <Label>Aktif</Label>
                <p className="text-xs text-muted-foreground">Duyuruyu yayınla/gizle</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingAnnouncement(null); resetForm(); }}>
              İptal
            </Button>
            <Button onClick={handleEdit} disabled={saving || !formData.title || !formData.content}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
